import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  LayoutGrid, 
  Settings, 
  HelpCircle, 
  LogOut,
  Plus,
  X
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  active?: boolean;
  hasAddButton?: boolean;
  onAddClick?: () => void;
  onClick?: () => void;
}

interface SidebarProps {
  onCloseMobile?: () => void;
}

const SidebarItem = ({ 
  icon, 
  label, 
  to, 
  active, 
  hasAddButton,
  onAddClick,
  onClick
}: SidebarItemProps) => {
  return (
    <li className="flex items-center relative">
      <Link
        to={to}
        className={cn(
          "flex items-center gap-3 py-3 px-4 rounded-md w-full transition-colors",
          active
            ? "text-primary bg-accent font-medium"
            : "text-muted-foreground hover:text-primary hover:bg-accent/50"
        )}
        onClick={onClick}
      >
        <span className="w-5 h-5 flex-shrink-0">{icon}</span>
        <span>{label}</span>
      </Link>
      {hasAddButton && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 absolute right-2 opacity-70 hover:opacity-100"
          onClick={onAddClick}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </li>
  );
};

export function Sidebar({ onCloseMobile }: SidebarProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const { signOut } = useAuth();
  
  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  return (
    <aside className="h-screen w-64 md:w-64 border-r border-border flex flex-col bg-card shadow-sm relative">
      {/* Close button for mobile view */}
      <Button 
        variant="ghost" 
        size="icon" 
        className="absolute top-4 right-4 lg:hidden" 
        onClick={onCloseMobile}
      >
        <X className="h-4 w-4" />
      </Button>
      
      <div className="p-5 border-b">
        <Link to="/" className="flex items-center gap-2 font-bold text-xl" onClick={onCloseMobile}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
          >
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="4" />
            <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
            <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
            <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
            <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
          </svg>
          <span>Standup Hiro</span>
        </Link>
      </div>
      
      <nav className="flex-1 py-5 overflow-y-auto">
        <ul className="space-y-0.5 px-3">
          <SidebarItem
            icon={<Home />}
            label="Home"
            to="/"
            active={currentPath === '/'}
            onClick={onCloseMobile}
          />
          <SidebarItem
            icon={<LayoutGrid />}
            label="Check Ins"
            to="/check-ins"
            active={currentPath.startsWith('/check-ins')}
            hasAddButton
            onAddClick={() => {}}
            onClick={onCloseMobile}
          />
          <SidebarItem
            icon={<Settings />}
            label="Settings"
            to="/settings"
            active={currentPath.startsWith('/settings')}
            onClick={onCloseMobile}
          />
        </ul>
      </nav>
      
      <div className="mt-auto p-4 border-t">
        <ul className="space-y-0.5">
          <SidebarItem
            icon={<HelpCircle />}
            label="Help & information"
            to="/help"
            active={currentPath.startsWith('/help')}
            onClick={onCloseMobile}
          />
          <li className="flex items-center relative">
            <Button
              variant="ghost"
              className="flex items-center gap-3 py-3 px-4 rounded-md w-full transition-colors justify-start text-muted-foreground hover:text-primary hover:bg-accent/50"
              onClick={handleLogout}
            >
              <span className="w-5 h-5 flex-shrink-0"><LogOut /></span>
              <span>Log out</span>
            </Button>
          </li>
        </ul>
      </div>
    </aside>
  );
}